import React, { useState, useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import CustomBetInsights from "../../../utils/CustomBetInsights";
import Loading from "../../../utils/Loading";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Input } from "@heroui/react";

ModuleRegistry.registerModules([AllCommunityModule]);

const BetInsightsTable = () => {
  const [bets, setBets] = useState([]);
  const [selectedOption, setSelectedOption] = useState(1);
  const [matchTypes, setMatchTypes] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("1");
  const [betDetails, setBetDetails] = useState(false);
  const [betDetailsAll, setBetDetailsAll] = useState([]);
  const [clientDetails, setClientDetails] = useState(false);
  const [clientDetailsAll, setClientDetailsAll] = useState([]);
  const [operatorList, setOperatorList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [digitsLoading, setDigitsLoading] = useState(true);
  const [selectedOperatorId, setSelectedOperatorId] = useState("");
  const [statusData, setStatusData] = useState([]);
  const bottomRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [statusFilter, setStatusFilter] = useState("1");
  const [selectedMatch, setSelectedMatch] = useState("1");
  const [matchList, setMatchList] = useState([]);
  const [closedType, setClosedType] = useState("0");
  const [selectedStatus, setSelectedStatus] = useState(1);
  const todayStr = new Date().toLocaleDateString("en-CA");
  const [betDate, setBetDate] = useState(
    new Date().toISOString().split("T")[0]
  ); // YYYY-MM-DD

  useEffect(() => {
    setStatusFilter("");
    setBetDate(new Date().toISOString().split("T")[0]); // reset date to today
  }, [selectedFilter]);
  useEffect(() => {
    if (matchList.length > 0) {
      const todayMatch = matchList.find((m) => m.date === todayStr);
      setSelectedMatch(todayMatch ? todayMatch.id : matchList[0].id);
    }
  }, [matchList, selectedFilter]);

  const COLORS = [
    "#2563EB", // Blue-600
    "#10B981", // Emerald
    "#F59E0B", // Amber
    "#6366F1", // Indigo
    "#EF4444", // Red-500
    "#3B82F6", // Blue-500
    "#F97316", // Orange-500
    "#22C55E", // Green-500
    "#EAB308", // Yellow-500
    "#A855F7", // Purple-500
    "#0EA5E9", // Sky-500
    "#EC4899", // Pink-500
    "#8B5CF6", // Violet-500
    "#14B8A6", // Teal-500
    "#F43F5E", // Rose-500
    "#84CC16", // Lime-500
    "#FACC15", // Yellow-400
    "#4ADE80", // Green-400
    "#FB923C", // Orange-400
    "#94A3B8", // Slate-400 (neutral for balance)
  ];
  const COLOURS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#A28BFE",
    "#FF4560",
  ];

  const filter = [
    { id: 1, name: "Match Type" },
    { id: 3, name: "Matches" },
    { id: 2, name: "Operator" },
  ];
  const statusOptions = [
    { id: 1, name: "All" },
    { id: 2, name: "Open" },
    { id: 3, name: "Settled" },
  ];
  const closedTypes = [
    { id: 0, name: "Open" },
    { id: 1, name: "Close" },
  ];
  const getOperatorStatus = async (id) => {
    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

    const dateQuery = betDate ? `?date=${encodeURIComponent(betDate)}` : "";
    const url = `${process.env.REACT_APP_API_HOST}/operator/${id}/status${dateQuery}`;

    const requestOptions = {
      method: "GET",
      headers: headers,
    };

    try {
      const response = await fetch(url, requestOptions);

      if (response.status === 401) {
        console.log("Unauthorized access. Please log in again.");
        return;
      }

      const result = await response.json();
      if (result?.data) {
        const statusData = result.data;
        setStatusData(statusData);
      }
    } catch (err) {
      console.log("Error fetching operator status:", err);
    }
  };
  const getMatchList = async (date, betType) => {
    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

    const url = `${process.env.REACT_APP_API_HOST}/betinsights/matches`;

    const payload = {
      date: date || new Date().toISOString().split("T")[0],
      is_close_type: betType ?? 0,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        console.log("Unauthorized access. Please log in again.");
        return;
      }

      const result = await response.json();

      if (result?.success && result?.data?.length > 0) {
        setMatchList(result.data);
      } else {
        // If no matches, set a default entry
        setMatchList([{ id: 0, name: "No matches" }]);
      }
    } catch (err) {
      console.log("Error fetching matches:", err);
      // Optional: show default entry on error
      setMatchList([{ id: 0, name: "No matches" }]);
    }
  };

  const gridRef = useRef(null);
  const onGridReady = (params) => {
    gridRef.current = params.api;
  };

  const handleExport = () => {
    if (gridRef.current) {
      gridRef.current.exportDataAsCsv({
        fileName: "Total_Bets_Details_export.csv",
        columnKeys: [
          "user_name",
          "name",
          "bet_type",
          "stake",
          "rate",
          "ip",
          "bet_time",
        ],
        onlySelected: false,
        processCellCallback: (params) => {
          if (params.column.getColId() === "bet_time" && params.value) {
            const date = new Date(params.value);
            const formatted = date.toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            });
            return formatted.replace(",", "");
          }
          return params.value;
        },
      });
    }
  };

  useEffect(() => {
    if (selectedFilter === "3") {
      getMatchList(betDate, closedType);
    } else {
      setMatchList([]);
    }
    if (selectedFilter === "2" && selectedOperatorId) {
      getOperatorStatus(selectedOperatorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, betDate]);

  const getOperatorsList = async () => {
    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

    const url = `${process.env.REACT_APP_API_HOST}/operat/list`;

    const requestOptions = {
      method: "GET",
      headers: headers,
    };

    try {
      const response = await fetch(url, requestOptions);

      if (response.status === 401) {
        console.log("Unauthorized access. Please log in again.");
        return;
      }

      const result = await response.json();
      if (result?.data) {
        result.data.forEach((item) => {
          const operatorId = item.operator_id;
          const opiId = item.id;
        });

        // Optional: store in state
        setOperatorList(result.data);
      } else {
        console.log("No operators found.");
      }
    } catch (err) {
      console.log("Error fetching operators list:", err);
    }
  };

  useEffect(() => {
    getOperatorsList();
  }, []);
  useEffect(() => {
    let timer;

    const fetchMatchTypes = async () => {
      let headers = new Headers();
      headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

      try {
        let apiUrl = `${process.env.REACT_APP_API_HOST}/matchTypes`;

        // If you're filtering by operator, add query param
        if (selectedFilter === "2" && selectedOperatorId) {
          apiUrl += `?operatorId=${selectedOperatorId}`;
        }

        const requestOptions = {
          method: "GET",
          headers: headers,
        };

        const res = await fetch(apiUrl, requestOptions);
        const data = await res.json();
        const types = data.data || [];

        setMatchTypes(types);
        setSelectedOption(types.length > 0 ? types[0].id : "");
      } catch (error) {
        console.error("Error fetching match types:", error);
      } finally {
        const timer = setTimeout(() => setLoading(false), 1000);
        return () => clearTimeout(timer);
      }
    };

    if (selectedFilter) {
      fetchMatchTypes();
    }

    return () => clearTimeout(timer);
  }, [selectedFilter, selectedOperatorId]);

  useEffect(() => {
    if (selectedFilter === "2") {
      setDigitsLoading(false);
      return;
    }

    // Only proceed if required values exist
    if (
      (selectedFilter === "1" && !selectedOption) ||
      (selectedFilter === "3" &&
        (!selectedOption || !selectedMatch || !betDate))
    ) {
      // waiting for required inputs (e.g., initial match types)
      setDigitsLoading(true);
      return;
    }

    const controller = new AbortController();

    const getDigits = async () => {
      try {
        setDigitsLoading(true);
        // Build POST body according to selectedFilter
        const payload = {};

        if (selectedFilter === "1") {
          payload.matchTypeId = selectedOption || "";
          payload.status = statusFilter || ""; // always include
          payload.bet_date = betDate || "";
        } else if (selectedFilter === "3") {
          payload.match = selectedMatch || "";
          payload.matchTypeId = selectedOption || "";
          payload.status = statusFilter || "";
          payload.bet_date = betDate || "";
          payload.closed_type = closedType || "";
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_HOST}/digit-stats`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("jwt"),
            },
            body: JSON.stringify(payload),
            signal: controller.signal, // attach the signal
          }
        );

        const result = await response.json();
        setBets(result.data || []);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error fetching digits:", err);
        }
      } finally {
        setDigitsLoading(false);
      }
    };

    getDigits();

    // Reset details
    setBetDetailsAll([]);
    setClientDetailsAll([]);
    setBetDetails(false);
    setClientDetails(false);

    // Cleanup cancels previous fetch if dependencies change
    return () => controller.abort();
  }, [
    selectedOption,
    statusFilter,
    selectedMatch,
    selectedFilter,
    selectedOperatorId,
    betDate,
    closedType,
  ]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const handleTotalBets = (rowData) => {
    setClientDetails(false);
    setBetDetailsAll(false);

    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

    // Build body conditionally
    //
    const bodyData = {
      user_id: localStorage.getItem("uid"),
      digit: rowData.digit,
      status: statusFilter,
      matchTypeId: selectedOption,
    };

    if (selectedFilter === "1") {
      bodyData.bet_date = betDate || "";
    }
    if (selectedFilter === "3") {
      bodyData.match = selectedMatch;
      bodyData.closedType = closedType;
    }
    fetch(`${process.env.REACT_APP_API_HOST}/getTotalNumberOfBets`, {
      method: "POST",
      headers,
      body: JSON.stringify(bodyData),
    })
      .then((res) =>
        res.status === 401 ? console.error("Unauthorized") : res.json()
      )
      .then((result) => {
        setBetDetailsAll(result.success ? result.data : []);
      })
      .catch((err) => console.error(err));

    setBetDetails(true);
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleUniqueUsers = (rowData) => {
    setBetDetails(false);
    setClientDetailsAll(false);

    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + localStorage.getItem("jwt"));

    const urlencoded = {
      user_id: localStorage.getItem("uid"),
      digit: rowData.digit,
      status: statusFilter,
      matchTypeId: selectedOption,
      ...(selectedFilter === "1" && { bet_date: betDate || "" }),
      ...(selectedFilter === "3" && { match: selectedMatch }),
    };

    let requestOptions = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(urlencoded),
    };

    fetch(process.env.REACT_APP_API_HOST + "/getUniqueClients", requestOptions)
      .then((response) => {
        if (response.status === 401) {
        } else {
          return response.json();
        }
      })
      .then((result) => {
        if (result.success === true) {
          const uniqueUsers = result.data.filter(
            (user, index, self) =>
              index === self.findIndex((u) => u.user_id === user.user_id)
          );
          setClientDetailsAll(uniqueUsers);
        }
      })
      .catch((err) => {})
      .finally(() => {});

    setClientDetails(true);
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  // const colDefs = useMemo(() => {
  //   if (selectedFilter === "2") {
  //     return [
  //       {
  //         headerName: "Total Users",
  //         field: "total_users",
  //         suppressMenu: true,
  //         filter: false,
  //         headerStyle: {
  //           backgroundColor: "#1c232f",
  //           color: "white",
  //           textAlign: "center",
  //         },
  //         cellStyle: { textAlign: "center" },
  //       },
  //       {
  //         headerName: "Total Bets",
  //         field: "total_bets",
  //         suppressMenu: true,
  //         filter: false,
  //         headerStyle: {
  //           backgroundColor: "#1c232f",
  //           color: "white",
  //           textAlign: "center",
  //         },
  //         cellStyle: { textAlign: "center" },
  //       },
  //       {
  //         headerName: "Total Digits",
  //         field: "total_digits",
  //         suppressMenu: true,
  //         filter: false,
  //         headerStyle: {
  //           backgroundColor: "#1c232f",
  //           color: "white",
  //           textAlign: "center",
  //         },
  //         cellStyle: { textAlign: "center" },
  //       },
  //       {
  //         headerName: "Total Amount",
  //         field: "total_amount",
  //         valueFormatter: (params) =>
  //           `₹${parseFloat(params.value || 0).toFixed(2)}`,
  //         suppressMenu: true,
  //         filter: false,
  //         headerStyle: {
  //           backgroundColor: "#1c232f",
  //           color: "white",
  //           textAlign: "center",
  //         },
  //         cellStyle: {
  //           textAlign: "center",
  //           color: "#22c55e",
  //           fontWeight: "bold",
  //         },
  //       },
  //     ];
  //   }

  //   // Digit-level default view
  //   return [
  //     {
  //       headerName: "Digit",
  //       field: "digit",
  //       suppressMenu: true,
  //       sortable: false,
  //       filter: false,
  //       headerStyle: {
  //         backgroundColor: "#1c232f",
  //         color: "white",
  //         textAlign: "center",
  //       },
  //     },
  //     {
  //       headerName: "Total Bets",
  //       field: "total_bets_on_digit",
  //       cellRenderer: CustomBetInsights,
  //       cellRendererParams: { showTotalButton: true, showUsersButton: false },
  //       suppressMenu: true,
  //       sortable: false,
  //       filter: false,
  //       headerStyle: {
  //         backgroundColor: "#1c232f",
  //         color: "white",
  //         textAlign: "center",
  //       },
  //     },
  //     {
  //       headerName: "Total Clients",
  //       field: "unique_users",
  //       cellRenderer: CustomBetInsights,
  //       cellRendererParams: { showTotalButton: false, showUsersButton: true },
  //       suppressMenu: true,
  //       sortable: false,
  //       filter: false,
  //       headerStyle: {
  //         backgroundColor: "#1c232f",
  //         color: "white",
  //         textAlign: "center",
  //       },
  //     },
  //     {
  //       headerName: "Total Amount",
  //       field: "total_stake_on_digit",
  //       valueFormatter: (params) =>
  //         `₹${parseFloat(params.value || 0).toFixed(2)}`,
  //       suppressMenu: true,
  //       sortable: false,
  //       filter: false,
  //       headerStyle: {
  //         backgroundColor: "#1c232f",
  //         color: "white",
  //         textAlign: "center",
  //       },
  //       cellStyle: {
  //         textAlign: "center",
  //         color: "#22c55e",
  //         fontWeight: "bold",
  //       },
  //     },
  //   ];

  //   if (selectedOption === "1") {
  //   colDefs.push({
  //     headerName: "Jodi",
  //     field: "jodi",
  //     valueFormatter: (params) => `₹${parseFloat(params.value || 0).toFixed(2)}`,
  //     suppressMenu: true,
  //     filter: false,
  //     headerStyle: { backgroundColor: "#1c232f", color: "white", textAlign: "center" },
  //     cellStyle: { textAlign: "center", color: "#22c55e", fontWeight: "bold" },
  //   });
  // }

  // }, [selectedFilter, handleTotalBets, handleUniqueUsers]);

  const colDefs = useMemo(() => {
    let cols = [];

    if (selectedFilter === "2") {
      cols = [
        {
          headerName: "Total Users",
          field: "total_users",
          suppressMenu: true,
          filter: false,
          headerStyle: {
            backgroundColor: "#1c232f",
            color: "white",
            textAlign: "center",
          },
          cellStyle: { textAlign: "center" },
        },
        {
          headerName: "Total Bets",
          field: "total_bets",
          suppressMenu: true,
          filter: false,
          headerStyle: {
            backgroundColor: "#1c232f",
            color: "white",
            textAlign: "right",
          },
          cellStyle: { textAlign: "right" },
          cellClass: "flex justify-start mt-8",
        },
        {
          headerName: "Total Digits",
          field: "total_digits",
          suppressMenu: true,
          filter: false,
          headerStyle: {
            backgroundColor: "#1c232f",
            color: "white",
            textAlign: "center",
          },
          cellStyle: { textAlign: "center" },
        },
        {
          headerName: "Total Amount",
          field: "total_amount",
          valueFormatter: (params) =>
            `₹${parseFloat(params.value || 0).toFixed(2)}`,
          suppressMenu: true,
          filter: false,
          headerStyle: {
            backgroundColor: "#1c232f",
            color: "white",
            textAlign: "center",
          },
          cellStyle: {
            textAlign: "center",
            color: "#22c55e",
            fontWeight: "bold",
          },
        },
      ];
    } else if (selectedFilter === "3") {
      // ✅ Filter 3 (already correct)
      cols = [
        {
          headerName: "Digit",
          field: "digit",
          suppressMenu: true,
          sortable: false,
          filter: false,
          headerStyle: {
            backgroundColor: "#1c232f",
            color: "white",
            textAlign: "center",
          },
        },
        {
          headerName: "Total Bets",
          field: "total_bets_on_digit",
          cellRenderer: CustomBetInsights,
          cellRendererParams: { showTotalButton: true, showUsersButton: false },
          suppressMenu: true,
          sortable: false,
          filter: false,
          statusFilter: statusFilter,
          cellClass: "center-cell",
          headerClass: "center-cell",
          headerStyle: {
            backgroundColor: "#1c232f",
            color: "white",
            textAlign: "center",
          },
        },
        {
          headerName: "Total Clients",
          field: "unique_users",
          cellRenderer: CustomBetInsights,
          cellRendererParams: { showTotalButton: false, showUsersButton: true },
          suppressMenu: true,
          sortable: false,
          filter: false,
          cellClass: "center-cell",
          headerClass: "center-cell",
          headerStyle: {
            backgroundColor: "#1c232f",
            color: "white",
            textAlign: "center",
          },
        },
        {
          headerName: "Total Amount",
          field: "total_stake_on_digit",
          valueFormatter: (params) =>
            `₹${parseFloat(params.value || 0).toFixed(2)}`,
          suppressMenu: true,
          sortable: false,
          filter: false,
          cellClass: "center-cell",
          headerClass: "center-cell",
          headerStyle: {
            backgroundColor: "#1c232f",
            color: "white",
            textAlign: "center",
          },
          cellStyle: {
            textAlign: "center",
            color: "#22c55e",
            fontWeight: "bold",
          },
        },
      ];
    } else {
      cols = [
        {
          headerName: "Digit",
          field: "digit",
          suppressMenu: true,
          sortable: false,
          filter: false,
          headerStyle: {
            backgroundColor: "#1c232f",
            color: "white",
            textAlign: "center",
          },
        },
        {
          headerName: "Total Bets",
          field: "total_bets_on_digit",
          cellRenderer: CustomBetInsights,
          cellRendererParams: { showTotalButton: true, showUsersButton: false },
          suppressMenu: true,
          sortable: false,
          filter: false,
          cellClass: "center-cell",
          headerClass: "center-cell",
          headerStyle: {
            backgroundColor: "#1c232f",
            color: "white",
            textAlign: "center",
          },
        },
        {
          headerName: "Total Clients",
          field: "unique_users",
          cellRenderer: CustomBetInsights,
          cellRendererParams: { showTotalButton: false, showUsersButton: true },
          suppressMenu: true,
          sortable: false,
          filter: false,
          cellClass: "center-cell",
          headerClass: "center-cell",
          headerStyle: {
            backgroundColor: "#1c232f",
            color: "white",
            textAlign: "center",
          },
        },
        {
          headerName: "Total Amount",
          field: "total_stake_on_digit",
          valueFormatter: (params) =>
            `₹${parseFloat(params.value || 0).toFixed(2)}`,
          suppressMenu: true,
          sortable: false,
          filter: false,
          headerStyle: {
            backgroundColor: "#1c232f",
            color: "white",
            textAlign: "center",
          },
          cellStyle: {
            textAlign: "center",
            color: "#22c55e",
            fontWeight: "bold",
          },
        },
      ];

      // ✅ Add Jodi only if selectedOption is "1"
      if (parseInt(selectedOption) === 1) {
        cols.push({
          headerName: "Jodi",
          field: "jodi",
          // valueGetter: (params) => {
          //   const jodi = parseFloat(params.data?.jodi || 0);
          //   const stake = parseFloat(params.data?.total_stake_on_digit || 0);
          //   return jodi + stake;
          // },
          valueFormatter: (params) =>
            `₹${parseFloat(params.value || 0).toFixed(2)}`,
          // valueFormatter: (params) =>
          //   `₹${parseFloat(params.value || 0).toFixed(2)}`,
          suppressMenu: true,
          filter: false,
          headerStyle: {
            backgroundColor: "#1c232f",
            color: "white",
            textAlign: "center",
            paddingLeft: "38px",
          },
          cellStyle: {
            textAlign: "center",
            color: "#22c55e",
            fontWeight: "bold",
          },
          cellStyle: { textAlign: "start" },
          hide: parseInt(selectedOption) !== 1,
        });
      }
    }

    return cols;
  }, [selectedFilter, selectedOption, handleTotalBets, handleUniqueUsers]);

  const colDefsBets = useMemo(
    () => [
      {
        headerName: "User Name",
        field: "user_name",
        cellRendererParams: {
          showTotalButton: true,
          showUsersButton: false,
        },
        suppressMenu: true,
        sortable: false,
        filter: false,
      },
      {
        headerName: "Match Name",
        field: "name",
        cellRendererParams: {
          showTotalButton: true,
          showUsersButton: false,
        },
        suppressMenu: true,
        sortable: false,
        filter: false,
      },
      {
        headerName: "Market Type",
        field: "bet_type",
        suppressMenu: true,
        sortable: false,
        filter: false,
      },
      {
        headerName: "Stake",
        field: "stake",
        cellRendererParams: {
          showTotalButton: true,
          showUsersButton: false,
        },
        suppressMenu: true,
        sortable: false,
        filter: false,
      },
      {
        headerName: "Rate",
        field: "rate",
        suppressMenu: true,
        sortable: false,
        filter: false,
      },
      {
        headerName: "IP",
        field: "ip",
        suppressMenu: true,
        sortable: false,
        filter: false,
      },
      {
        headerName: "Placed At",
        field: "bet_time",
        cellRenderer: (params) => {
          const date = new Date(params.value);
          return date.toLocaleString("en-GB");
        },
        suppressMenu: true,
        sortable: false,
        filter: false,
      },
    ],
    []
  );

  const colDefsUsers = useMemo(
    () => [
      {
        headerName: "Operator Id",
        field: "operator_id",
        suppressMenu: true,
        sortable: false,
        filter: false,
      },
      {
        headerName: "Username",
        field: "user_name",
        cellRendererParams: {
          showTotalButton: true,
          showUsersButton: false,
        },
        suppressMenu: true,
        sortable: false,
        filter: false,
      },
      {
        headerName: "UserId",
        field: "user_id",
        cellRendererParams: {
          showTotalButton: true,
          showUsersButton: false,
        },
        suppressMenu: true,
        sortable: false,
        filter: false,
      },
    ],
    []
  );

  const defaultColDef = {
    flex: 1,
    sortable: true,
    // resizable: true,
    cellClass: "even:bg-gray-50 odd:bg-white",
  };
  // Responsive font size for Pie labels
  const getPieLabelFontSize = () => {
    if (window.innerWidth < 500) return 8;
    if (window.innerWidth < 768) return 10;
    if (window.innerWidth < 1024) return 8;
    return 14;
  };
  // Responsive Pie label: hide on very small screens
  const renderPieLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    name,
    value,
  }) => {
    if (window.innerWidth < 400) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 1.2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="#333"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={getPieLabelFontSize()}
        stroke="#fff"
        strokeWidth={2}
        paintOrder="stroke"
      >
        {`${name}: ${value}`}
      </text>
    );
  };
  const pieData = (statusData?.result || []).map((item) => ({
    ...item,
    total_amount: parseFloat(item.total_amount),
  }));
  if (loading) {
    return <Loading />;
  }
  return (
    <div>
      <div id="header-part">
        <div className="flex">
          {/* <div className="mt-2">
            {" "}
            <label className="mr-4 font-semibold">Filter by:</label>
          </div> */}
          <div>
            <div class="flex">
              <div>
                <label className=" block text-sm  text-gray-700 mb-1 font-semibold">
                  Filter by:
                </label>
                <select
                  value={selectedFilter}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    setSelectedFilter(selectedValue);
                    setSelectedOption(""); // Reset match type
                    setBets([]);
                    if (selectedValue === "2" && operatorList.length > 0) {
                      setSelectedOperatorId(operatorList[0].id);
                      getOperatorStatus(operatorList[0].id);
                    }
                  }}
                  className="p-2 border border-gray-300 rounded ml-2"
                >
                  {filter.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
              <div></div>
              <div></div>
            </div>
          </div>
          <div>
            {/* Show Match Type dropdown only if Filter is Match Types (id: 1) */}
            {selectedFilter === "1" && (
              <>
                <div className="flex">
                  <div className="px-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date:
                    </label>
                    <Input
                      type="date"
                      name="bet_date"
                      value={betDate}
                      onChange={(e) => setBetDate(e.target.value)}
                      className="border border-gray-300  bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className=" ml-4 block text-sm  text-gray-700 mb-1 font-semibold">
                      Match Type:
                    </label>
                    <select
                      value={selectedOption}
                      onChange={(e) => setSelectedOption(e.target.value)}
                      className="p-2 border border-gray-300 rounded ml-2"
                    >
                      {matchTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className=" block text-sm  text-gray-700 mb-1 font-semibold">
                      Status:
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="p-2 border border-gray-300 rounded ml-2"
                    >
                      {statusOptions.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            {selectedFilter === "2" && (
              <>
                <div className="flex">
                <div className="px-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date:
                    </label>
                    <Input
                      type="date"
                      name="bet_date"
                      value={betDate}
                      onChange={(e) => setBetDate(e.target.value)}
                      className="border border-gray-300  bg-white"
                      required
                    />
                </div>
                <div>
                  <label className="block ml-2 text-sm font-medium text-gray-700 mb-1">
                    Operator:
                  </label>
                  <select
                    value={selectedOperatorId}
                    onChange={(e) => {
                      const id = e.target.value;
                      if (!id) return;

                      setSelectedOperatorId(id);
                      getOperatorStatus(id);
                      setSelectedOption(""); // Reset match type
                      setBets([]);
                    }}
                    className="p-2 border border-gray-300 rounded ml-2"
                  >
                    {operatorList.map((op) => (
                      <option key={op.id} value={op.id}>
                        {op.operator_id}
                      </option>
                    ))}
                  </select>
                </div>
                </div>
              </>
            )}
            {selectedFilter === "3" && (
              <>
                <div className="flex gap-1">
                  <div className="px-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date:
                    </label>
                    <Input
                      type="date"
                      name="bet_date"
                      value={betDate} // bind to state
                      onChange={(e) => setBetDate(e.target.value)} // update state on change
                      className="border border-gray-300  bg-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bet Type:
                    </label>
                    <select
                      value={closedType}
                      onChange={(e) => setClosedType(e.target.value)}
                      className="p-2 border border-gray-300 rounded ml-2"
                    >
                      {closedTypes.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Matches:
                    </label>
                    <select
                      value={selectedMatch}
                      onChange={(e) => setSelectedMatch(e.target.value)}
                      className="p-2 border border-gray-300 rounded ml-2"
                    >
                      {matchList.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Match Type:
                    </label>
                    <select
                      value={selectedOption}
                      onChange={(e) => setSelectedOption(e.target.value)}
                      className="p-2 border border-gray-300 rounded"
                    >
                      {matchTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status:
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="p-2 border border-gray-300 rounded ml-2"
                    >
                      {statusOptions.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div></div>
              </>
            )}
          </div>
        </div>
      </div>
      <div id="body-part">
        <div
          className={`grid grid-cols-12 gap-6 min-h-[400px] ${
            selectedFilter === "3" ? "mt-6" : "mt-1"
          }`}
        >
          {/* For Match Type filter */}
          {selectedFilter === "1" && (
            <>
              <div className="col-span-12 xl:col-span-6 relative">
                <div
                  className="ag-theme-alpine bg-blue-950 overflow-x-auto rounded-lg mt-0"
                  style={{ height: 800, minWidth: 0 }}
                >
                  {digitsLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center ">
                      <Loading />
                    </div>
                  )}
                  <AgGridReact
                    rowData={bets}
                    columnDefs={colDefs}
                    defaultColDef={defaultColDef}
                    overlayNoRowsTemplate={"<span></span>"}
                    suppressNoRowsOverlay={digitsLoading || loading}
                    pagination={true}
                    paginationPageSize={100}
                    rowHeight={50}
                    enableCellTextSelection={true}
                    context={{
                      handleTotalBets,
                      handleUniqueUsers,
                    }}
                  />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-6">
                <div className="w-full md:w-full bg-white rounded shadow p-4">
                  <h3 className="text-lg font-semibold mb-4">
                    Total Bets Distribution
                  </h3>
                  <div className="w-full" style={{ minWidth: 0 }}>
                    <ResponsiveContainer
                      width="100%"
                      height={window.innerWidth < 500 ? 300 : 725}
                    >
                      <PieChart width={400} height={400}>
                        <Pie
                          data={bets}
                          dataKey="total_bets_on_digit"
                          nameKey="digit"
                          cx="50%"
                          cy="50%"
                          innerRadius={window.innerWidth < 500 ? 50 : 110}
                          outerRadius={window.innerWidth < 500 ? 90 : 180}
                          paddingAngle={4}
                          labelLine={window.innerWidth >= 400}
                          label={renderPieLabel}
                          stroke="#fff"
                          strokeWidth={2}
                        >
                          {bets.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name, props) => [
                            `${value} bets`,
                            `Digit ${props.payload?.digit}`,
                          ]}
                          contentStyle={{
                            borderRadius: "8px",
                            backgroundColor: "#f9f9f9",
                          }}
                        />
                        {windowWidth >= 1024 && (
                          <Legend
                            layout="horizontal"
                            align="center"
                            verticalAlign="bottom"
                            formatter={(value, entry) =>
                              ` ${entry.payload?.digit || value} - ${
                                entry.payload?.total_bets_on_digit || 0
                              }  bets`
                            }
                            wrapperStyle={{
                              fontSize: window.innerWidth < 500 ? 10 : 14,
                            }}
                          />
                        )}
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* For Operator filter */}
          {selectedFilter === "2" && (
            <>
              <div className="col-span-12 xl:col-span-6">
                <div
                  className="ag-theme-alpine bg-blue-950 overflow-x-auto rounded-lg"
                  style={{ height: 800, minWidth: 0 }}
                >
                  <AgGridReact
                    rowData={
                      selectedFilter === "2" && statusData?.result
                        ? statusData.result
                        : []
                    }
                    columnDefs={colDefs}
                    defaultColDef={defaultColDef}
                    pagination={true}
                    enableCellTextSelection={true}
                    paginationPageSize={100}
                    rowHeight={50}
                  />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-6">
                <div className="w-full md:w-full bg-white rounded shadow p-4">
                  <h3 className="text-lg font-semibold mb-4">
                    Operator Bets Distribution
                  </h3>
                  <div className="w-full" style={{ minWidth: 0 }}>
                    <ResponsiveContainer
                      width="100%"
                      height={window.innerWidth < 500 ? 300 : 725}
                    >
                      <PieChart width={400} height={400}>
                        <Pie
                          data={pieData}
                          dataKey="total_amount"
                          nameKey="total_users"
                          cx="50%"
                          cy="50%"
                          innerRadius={window.innerWidth < 500 ? 50 : 110}
                          outerRadius={window.innerWidth < 500 ? 90 : 180}
                          paddingAngle={4}
                          labelLine={window.innerWidth >= 400}
                          label={({ name, value }) => `${name}: ₹${value}`}
                          stroke="#fff"
                          strokeWidth={2}
                        >
                          {pieData.map((_, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLOURS[index % COLOURS.length]}
                            />
                          ))}
                        </Pie>

                        <Tooltip
                          formatter={(value, name) => [`₹${value}`, `${name}`]}
                          contentStyle={{
                            borderRadius: "8px",
                            backgroundColor: "#f9f9f9",
                          }}
                        />

                        <Legend
                          layout="horizontal"
                          align="center"
                          verticalAlign="bottom"
                          formatter={(value, entry) =>
                            `${entry.payload?.total_users || value} - ₹${
                              entry.payload?.total_amount
                            }`
                          }
                          wrapperStyle={{
                            fontSize: window.innerWidth < 500 ? 10 : 14,
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </>
          )}

          {selectedFilter === "3" && (
            <div className="col-span-12">
              <div
                className="ag-theme-alpine bg-blue-950 overflow-x-auto rounded-lg"
                style={{ height: 800, minWidth: 0 }}
              >
                <AgGridReact
                  rowData={bets}
                  columnDefs={colDefs}
                  defaultColDef={defaultColDef}
                  pagination={true}
                  enableCellTextSelection={true}
                  paginationPageSize={100}
                  rowHeight={50}
                  context={{
                    handleTotalBets,
                    handleUniqueUsers,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`
  /* Force center alignment for AG Grid cells and headers */
  .ag-theme-alpine .ag-cell.center-cell .ag-cell-wrapper {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
  }
  .ag-theme-alpine .ag-header-cell.center-cell .ag-header-cell-label {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
  }
  @media (max-width: 600px) {
    .ag-theme-alpine .ag-paging-panel {
      font-size: 12px !important;
      padding: 4px 2px !important;
      flex-wrap: wrap;
      justify-content: center;
    }
    .ag-theme-alpine .ag-paging-panel > * {
      margin: 2px 4px !important;
    }
  }
`}</style>
      <div className="px-1 md:px-0 lg:px-1 xl:px-2 mx-auto max-w-screen-xl md:max-w-screen-2xl 2xl:max-w-screen-2xl lg:py-4">
        {betDetails && (selectedFilter === "1" || selectedFilter === "3") && (
          <div className="mt-6 w-full">
            <div className="flex flex-col">
              {/* TABLE FOR BET DETAILS */}
              {/* <div className="font-bold mb-2">BET DETAILS</div> */}
              <div className="mb-2 flex justify-end">
                <button
                  onClick={handleExport}
                  className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-md hover:bg-blue-700 transition-all duration-150 shadow-sm"
                >
                  <span>Export</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-5 h-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                    />
                  </svg>
                </button>
              </div>
              <div
                className="ag-theme-alpine bg-blue-950"
                style={{ height: 500 }}
              >
                <AgGridReact
                  rowData={betDetailsAll}
                  columnDefs={colDefsBets}
                  defaultColDef={defaultColDef}
                  pagination={true}
                  onGridReady={onGridReady}
                  enableCellTextSelection={true}
                  paginationPageSize={100}
                  rowHeight={60}
                  context={
                    {
                      // handleTotalBets,
                      // handleUniqueUsers,
                    }
                  }
                />
              </div>
            </div>
          </div>
        )}
        {clientDetails &&
          (selectedFilter === "1" || selectedFilter === "3") && (
            <div className="mt-6 w-full">
              <div className="flex flex-col">
                {/* TABLE FOR CLIENT DETAILS */}
                {/* <div className="font-bold mb-2">CLIENT DETAILS</div> */}
                <div
                  className="ag-theme-alpine bg-blue-950"
                  style={{ height: 500 }}
                >
                  <AgGridReact
                    rowData={clientDetailsAll}
                    columnDefs={colDefsUsers}
                    defaultColDef={defaultColDef}
                    pagination={true}
                    enableCellTextSelection={true}
                    paginationPageSize={100}
                    rowHeight={60}
                    context={
                      {
                        // handleTotalBets,
                        // handleUniqueUsers,
                      }
                    }
                  />
                </div>
              </div>
            </div>
          )}
        <div ref={bottomRef} />{" "}
      </div>
    </div>
  );
};

export default BetInsightsTable;
